import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {AuthStateService} from '@auth/states';
import {CompanyStateService} from '@core/states';
import {ApiCollection, ApiResponse, CompliancePlan, PlanCalendarEntry, PlanRiskComparisonRow} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  private readonly planCacheTtlMs = 15000;
  private readonly planCache = new Map<string, {at: number; result: CompliancePlan}>();
  private readonly planInFlight = new Map<string, Promise<ApiResponse<CompliancePlan>>>();

  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getPlans(page = 1, itemsPerPage = 20): Promise<ApiResponse<CompliancePlan[]>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLANS, {page, itemsPerPage});
      const headers = this.buildHeaders();
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const plans = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapPlan(item));
      return {success: true, result: plans};
    } catch (error: any) {
      log.error('Error loading plans:', error);
    }
    return {success: false, message: 'No se pudieron cargar los planes.'};
  }

  async getPlan(planId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<CompliancePlan>> {
    const key = String(planId);
    const cached = this.planCache.get(key);
    if (cached && (Date.now() - cached.at) < this.planCacheTtlMs) {
      return {success: true, result: cached.result};
    }

    const pending = this.planInFlight.get(key);
    if (pending) {
      return pending;
    }

    const requestPromise = this.fetchPlan(planId, options).finally(() => {
      this.planInFlight.delete(key);
    });
    this.planInFlight.set(key, requestPromise);
    const response = await requestPromise;
    if (response.success && response.result) {
      this.planCache.set(key, {at: Date.now(), result: response.result});
    }
    return response;
  }

  private async fetchPlan(planId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<CompliancePlan>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLAN_DETAIL, {id: planId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapPlan(response)};
    } catch (error: any) {
      log.error('Error loading plan:', error);
    }
    return {success: false, message: 'No se pudo cargar el plan.'};
  }

  async createPlan(payload: {
    name: string;
    description?: string;
    goals?: string;
    startDate?: string | null;
    endDate?: string | null;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<CompliancePlan>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLANS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, payload, headers ? {headers} : undefined);
      return {success: true, result: this.mapPlan(response)};
    } catch (error: any) {
      log.error('Error creating plan:', error);
    }
    return {success: false, message: 'No se pudo crear el plan.'};
  }

  async updatePlan(planId: string | number, payload: {
    name?: string;
    description?: string;
    goals?: string;
    startDate?: string | null;
    endDate?: string | null;
    status?: string;
    closingPhase?: 'resend' | 'results' | 'controls' | 'schedule' | null;
    phaseOneLocked?: boolean;
    phaseOneSurveyIds?: Array<string | number>;
    phaseOneDeclarationIds?: Array<string | number>;
    phaseOneRiskSnapshot?: Array<{id: string | number; concept: string; current: number; projected: number}>;
    phaseOneSurveyDispatches?: Array<{surveyId: string | number; dispatchKey: string}>;
    phaseThreeControlDecisions?: Array<{controlId: string | number; decision: 'inherit' | 'promote' | 'finalize'}>;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<CompliancePlan>> {
    try {
      const key = String(planId);
      this.planCache.delete(key);
      this.planInFlight.delete(key);
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLAN_DETAIL, {id: planId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(url, payload, {headers, json: false});
      if (!response) {
        const refreshed = await this.fetchPlan(planId, options);
        if (refreshed.success && refreshed.result) {
          this.planCache.set(key, {at: Date.now(), result: refreshed.result});
        }
        if (refreshed.success) return refreshed;
        return {success: true};
      }
      const mapped = this.mapPlan(response);
      this.planCache.set(key, {at: Date.now(), result: mapped});
      return {success: true, result: mapped};
    } catch (error: any) {
      log.error('Error updating plan:', error);
    }
    return {success: false, message: 'No se pudo actualizar el plan.'};
  }

  async addPlanTasks(planId: string | number, taskIds: Array<string | number>): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLAN_TASKS, {id: planId});
      const headers = this.buildHeaders();
      await this.apiService.post(url, {tasks: taskIds}, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error associating tasks to plan:', error);
    }
    return {success: false, message: 'No se pudieron asociar las tareas.'};
  }

  async closeAndOpenPlan(planId: string | number, payload: {
    name: string;
    description?: string;
    goals?: string;
    startDate?: string | null;
    endDate?: string | null;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<CompliancePlan>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLAN_CLOSE_AND_OPEN, {id: planId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, payload, headers ? {headers} : undefined);
      this.planCache.delete(String(planId));
      this.planInFlight.delete(String(planId));
      return {success: true, result: this.mapPlan(response)};
    } catch (error: any) {
      log.error('Error closing/opening compliance plan:', error);
    }
    return {success: false, message: 'No se pudo cerrar y abrir el plan de forma atómica.'};
  }

  async getPlanCalendar(planId: string | number): Promise<ApiResponse<PlanCalendarEntry[]>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLAN_CALENDAR, {id: planId});
      const headers = this.buildHeaders();
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const entries = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapCalendarEntry(item));
      return {success: true, result: entries};
    } catch (error: any) {
      log.error('Error loading plan calendar:', error);
    }
    return {success: false, message: 'No se pudo cargar el calendario del plan.'};
  }

  async getRiskComparison(planId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<PlanRiskComparisonRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANCE_PLAN_RISK_COMPARISON, {id: planId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const rows = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapRiskComparison(item));
      return {success: true, result: rows};
    } catch (error: any) {
      log.error('Error loading plan risk comparison:', error);
    }
    return {success: false, message: 'No se pudo cargar la comparativa de riesgo.'};
  }

  private mapPlan(item: any): CompliancePlan {
    return {
      id: item.id,
      name: item.name,
      description: item.description,
      goals: item.goals || item.objectives,
      startDate: item.startDate || item.start_at || item.start,
      endDate: item.endDate || item.end_at || item.end,
      status: item.status
      ,
      closingPhase: item.closingPhase ?? null,
      phaseOneLocked: Boolean(item.phaseOneLocked),
      phaseOneSurveyIds: Array.isArray(item.phaseOneSurveyIds) ? item.phaseOneSurveyIds : [],
      phaseOneDeclarationIds: Array.isArray(item.phaseOneDeclarationIds) ? item.phaseOneDeclarationIds : [],
      phaseOneRiskSnapshot: Array.isArray(item.phaseOneRiskSnapshot)
        ? item.phaseOneRiskSnapshot.map((row: any) => ({
          id: row.id,
          concept: row.concept,
          current: Number(row.current || 0),
          projected: Number(row.projected || 0),
        }))
        : [],
      phaseOneSurveyDispatches: Array.isArray(item.phaseOneSurveyDispatches)
        ? item.phaseOneSurveyDispatches
          .map((row: any) => ({
            surveyId: row.surveyId,
            dispatchKey: String(row.dispatchKey || '').trim(),
          }))
          .filter((row: any) => row.dispatchKey !== '')
        : [],
      phaseThreeControlDecisions: Array.isArray(item.phaseThreeControlDecisions)
        ? item.phaseThreeControlDecisions
          .map((row: any) => ({
            controlId: row.controlId,
            decision: String(row.decision || '').trim(),
          }))
          .filter((row: any) => row.controlId !== undefined && ['inherit', 'promote', 'finalize'].includes(row.decision))
        : [],
    };
  }

  private mapCalendarEntry(item: any): PlanCalendarEntry {
    const rootTaskId = item.id ?? item.task?.id ?? item.taskId;
    const rootTaskName = item.name ?? item.task?.name ?? item.taskName;
    const date = item.date || item.day || item.startDate || item.endDate || item.start_at || item.end_at;
    return {
      date,
      taskId: rootTaskId,
      taskName: rootTaskName
    };
  }

  private mapRiskComparison(item: any): PlanRiskComparisonRow {
    return {
      concept: item.concept || item.name,
      id: item.id,
      current: Number(item.current ?? item.actual ?? item.values?.[0] ?? 0),
      projected: Number(item.projected ?? item.expected ?? item.values?.[1] ?? 0)
    };
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      return skipLoader ? new HttpHeaders().set('X-Skip-Loader', '1') : null;
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      return skipLoader ? new HttpHeaders().set('X-Skip-Loader', '1') : null;
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
